import { HTMLAttributes, useMemo } from 'react'
import { Box, Container, Grid, Skeleton, SvgIcon, Typography } from '@mui/material'

import Button from '@/components/base/button'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'
import { isPostAvailable } from '@/utils/date'
import { formatPrice, truncateText } from '@/utils/strings'

import { PostSearchParams, usePostsSearch } from '@/api/posts'
import { RequestParams, WPLink } from '@/types'

export type StudentRequestsProps = HTMLAttributes<HTMLDivElement> & {
  title: string
  link: WPLink
  request_params: RequestParams
  is_first?: boolean
}

const StudentRequests = ({
  title,
  link,
  request_params,
  is_first,
  ...props
}: StudentRequestsProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { strings } = useAppStore()

  const postsParams = useMemo<PostSearchParams | undefined>(
    () =>
      currentLocale
        ? {
            city_id:
              request_params.city_id && request_params.city_id !== 'null'
                ? parseInt(request_params.city_id)
                : undefined,
            order_by: request_params.order_by as PostSearchParams['order_by'],
            per_page: request_params.per_page,
            locale: currentLocale
          }
        : undefined,

    [request_params, currentLocale]
  )

  const { collection: rawItems, isLoading: loading } = usePostsSearch(postsParams)
  const items = useMemo(() => rawItems?.filter((item) => isPostAvailable(item.available_to)), [rawItems])

  const renderSkeleton = (key: string) => (
    <Grid size={{ xs: 4, sm: 4, md: 4 }} key={key}>
      <Box
        sx={(theme) => ({
          p: 2,
          pb: 4,
          borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius8,
          background: isHappa ? 'white' : theme.palette.grey[100],
          boxShadow: isHappa ? theme.shadows[2] : 'none',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2
        })}
      >
        <Skeleton variant="circular" width={40} height={40} />
        <Box
          sx={() => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            width: '100%'
          })}
        >
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="100%" height={42} />
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="100%" height={42} />
        </Box>
      </Box>
    </Grid>
  )

  return (
    <Box {...props}>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 12 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              {title ? (
                <Typography
                  variant="h3"
                  component={is_first ? 'h1' : 'h2'}
                  fontWeight={isHappa ? '700' : '400'}
                  color={isHappa ? 'primary' : '#212121'}
                >
                  {title}
                </Typography>
              ) : null}
              {link?.url ? (
                <Button href={link.url} variant="contained" color="info" icon="plus">
                  {link.title}
                </Button>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Spacer size="xs" />
      <Container>
        <Grid container>
          {loading
            ? Array.from({ length: request_params?.per_page || 3 }).map((_, i) =>
                renderSkeleton(`skeleton-${i}`)
              )
            : items?.map((item) => (
                <Grid key={item.id} size={{ xs: 4, sm: 4, md: 4 }}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      pb: 4,
                      borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius8,
                      background: isHappa ? 'white' : theme.palette.grey[100],
                      boxShadow: isHappa ? theme.shadows[2] : 'none',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2
                    })}
                  >
                    <Box
                      sx={(theme) => ({
                        background: theme.palette.primary.main,
                        color: theme.palette.surface.background,
                        width: '4rem',
                        height: '4rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '100px'
                      })}
                    >
                      <SvgIcon>
                        <use href="#ico-student" />
                      </SvgIcon>
                    </Box>
                    <Box
                      sx={() => ({
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 2
                      })}
                    >
                      {item.user?.first_name || item.user?.last_name ? (
                        <Typography
                          variant="textLarge"
                          fontWeight={isHappa ? '700' : '500'}
                          component="span"
                          sx={{
                            fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)'
                          }}
                        >
                          {[item.user?.first_name, item.user?.last_name].filter(Boolean).join(' ')}
                        </Typography>
                      ) : null}
                      {item.city?.name?.[currentLocale!] ? (
                        <Typography variant="textRegular" color="text.secondary" component="span">
                          {item.city.name[currentLocale!]}
                        </Typography>
                      ) : null}
                      {item.price ? (
                        <Typography
                          variant="textLarge"
                          color="primary"
                          fontWeight={500}
                          component="span"
                          sx={{
                            fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)'
                          }}
                        >
                          {`${formatPrice(item.price)}/${strings?.labels?.month}`}
                        </Typography>
                      ) : null}
                      {item.description?.[currentLocale!] ? (
                        <Typography variant="textRegular" color="text.secondary" component="p">
                          {truncateText(item.description[currentLocale!], 200)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default StudentRequests
